import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
    DeleteRecord,
    GetListview,
    SuccessAlert,
    URLPRIFIX,
    ErrorAlert,
    GetStatusCounts,
    RupeeFormat
} from "../../../functions/api";
import { useStore } from "../../../Components/StateProvider";
import Pagination from "../../../Components/Fields/Pagination";
import StatusTabs from "../../../Components/Elements/StatusTabs";
import BulkActionsBar from "../../../Components/Elements/BulkActionsBar";
import ListViewUp from "../../../Components/Elements/ListViewUp";
import {
    URL_Account_Journal_List,
} from "../../../Constancs/apiconst";

const JournalsListUpdated = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const { companyId } = useParams();
    
    // Config Constants
    const tablename = "2/accounts/journals";
    const tableURL = URL_Account_Journal_List + companyId;
    const DeleteURL = "/api/v2/journals/delete/"; // Adjust to your actual delete endpoint
    const statusCountURL = "/api/v2/journalentry/getstatuscount/" + companyId;

    // State
    const [activeTab, setActiveTab] = useState("ALL");
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [hasTabClicked, setHasTabClicked] = useState(false);
    const [filters, setFilters] = useState({});
    const [statusTabs, setStatusTabs] = useState([]);
    const [sortConfig, setSortConfig] = useState({ field: "createdDate", direction: "desc" });

    const store = useStore();

    const Tablehead = " pr-[10px] max-sm:text-[26px] md:text-[20px] lg:text-[16px] xl:text-[20px] font-bold ";
    const Tablerecord = " pr-[10px] max-sm:text-[26px] md:text-[20px] lg:text-[12px] xl:text-[16px] overflow-hidden";

    const header = [
        ["Date", "createdDate"],
        ["Journal Entry", "referenceNumber"],
        ["From Account", "fromAccount"],
        ["To Account", "toAccount"],
        ["Amount", "amount"],
        ["Status", "status"],
        ["Action", "ACTION"],
    ];

    const init = {
        filterfield: "all",
        search: "",
        sort: "desc",
        currentpage: 1,
        totalpage: 1,
        pagesize: 20,
    };

    // --- Actions & Data Fetching ---

    const fetchStatusCounts = useCallback(async () => {
        try {
            const counts = await GetStatusCounts(statusCountURL, store);
            if (counts && counts.statuscount) {
                setStatusTabs(counts.statuscount);
            }
        } catch (error) {
            console.error("Error fetching status counts:", error);
        }
    }, [store, statusCountURL]);

    const loadListview = useCallback((params = {}) => {
        GetListview({
            listUrl: tableURL,
            store: store,
            field: params.field !== undefined ? params.field : store.getvalue("filterfield"),
            search: params.search !== undefined ? params.search : store.getvalue("search"),
            sort: params.sort !== undefined ? params.sort : store.getvalue("sort"),
            sortfield: params.sortfield !== undefined ? params.sortfield : sortConfig.field,
            page: params.page !== undefined ? params.page : store.getvalue("currentpage"),
            pagesize: params.pagesize !== undefined ? params.pagesize : store.getvalue("pagesize"),
        });
    }, [tableURL, store, sortConfig.field]);

    useEffect(() => {
        store.setmodule("journals", init).then(() => {
            store.stopLoading();
            store.setheader("Journal List");
            store.setvalue("defaultpagesize", init.pagesize);
            fetchStatusCounts();
            
            const states = store?.state?.records?.journals;
            loadListview({
                field: queryParams.get("field") || states?.filterfield,
                search: queryParams.get("search") || states?.search,
                sort: queryParams.get("sort") || states?.sort,
                sortfield: queryParams.get("sortfield") || states?.sortfield || sortConfig.field,
                pagesize: queryParams.get("pagesize") || states?.pagesize,
            });
        });
    }, []);

    const change = useCallback((props) => {
        store.setvalue("filterfield", props.field || "all");
        store.setvalue("search", props.search || "");
        store.setvalue("pagesize", props.pagesize || store.getvalue("defaultpagesize"));
        store.setvalue("sort", props.sort || init.sort);
        store.setvalue("currentpage", 1);

        loadListview({
            field: props.field,
            search: props.search,
            sort: props.sort,
            sortfield: props.sortfield || sortConfig.field,
            page: 1,
            pagesize: props.pagesize,
        });
    }, [loadListview, store, init.sort, sortConfig.field]);

    const handleSort = useCallback((label) => {
        const newDirection = sortConfig.field === label && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ field: label, direction: newDirection });
        change({
            field: store.getvalue("filterfield"),
            search: store.getvalue("search"),
            sort: newDirection,
            sortfield: label,
            pagesize: store.getvalue("pagesize"),
        });
    }, [change, sortConfig, store]);

    const handleFilterChange = useCallback((label, newValue) => {
        const updatedFilters = { ...filters, [label]: newValue };
        setFilters(updatedFilters);
        change({
            field: label,
            search: newValue,
            pagesize: store.getvalue("pagesize"),
            sort: sortConfig.direction,
            sortfield: sortConfig.field,
        });
    }, [change, filters, sortConfig.direction, sortConfig.field, store]);

    // --- Selection Logic ---

    const handleOnSelect = useCallback((itemOrItems, isChecked) => {
        if (Array.isArray(itemOrItems)) {
            setSelectedRecords(isChecked ? [...itemOrItems] : []);
        } else {
            setSelectedRecords((prev) => 
                isChecked ? [...prev, itemOrItems] : prev.filter((r) => r.transactionId !== itemOrItems.transactionId)
            );
        }
    }, []);

    const isSelected = useCallback((item) => 
        selectedRecords.some((sItem) => sItem.transactionId === item.transactionId), 
    [selectedRecords]);

    // --- Navigation ---

    const handleClickRow = useCallback((item) => {
        const typeId = item.transactionTypeId;
        const id = item.transactionId;
        let path = "";

        switch (typeId) {
            case 1: path = "invoice"; break;
            case 2: path = "journal"; break;
            case 3: path = "expense"; break;
            case 4: path = "receipt"; break;
            case 7: path = "payable"; break;
            default: path = "journal";
        }

        store.navTo(`/${URLPRIFIX}/${companyId}/accounts/${path}/view/${id}`);
    }, [companyId, store]);

    // --- UI Helpers ---

    const records = (store.getvalue("list") || []).map((record) => ({
        ...record,
        id: record.transactionId, // Ensure ListViewUp has a unique 'id'
        status: record.status?.toUpperCase() || "",
        debit: RupeeFormat(record.debit),
        credit: RupeeFormat(record.credit),
    }));

    const handleTabClick = useCallback((status, count) => {
        setActiveTab(status);
        setHasTabClicked(true);
        const isAll = status === "ALL";
        change({
            field: isAll ? "" : "status",
            search: isAll ? "" : status,
            pagesize: count || store.getvalue("pagesize"),
            sort: store.getvalue("sort"),
        });
    }, [change, store]);

    return (
        <div className="grid grid-rows-10 h-full">
            <div className="flex flex-wrap justify-between items-center mb-2 px-2">
                <StatusTabs
                    statusTabs={statusTabs}
                    activeTab={activeTab}
                    onTabClick={handleTabClick}
                    hasTabClicked={hasTabClicked}
                    onClearClick={() => {
                        setActiveTab("ALL");
                        setFilters({});
                        setHasTabClicked(false);
                        change({ field: "all", search: "", pagesize: init.pagesize });
                    }}
                />

                {store.getprops("add") && (
                    <button
                        className="bg-primary text-secondary px-4 py-2 rounded-full transition text-sm font-semibold"
                        onClick={() => {
                            store.removemodule("journals");
                            store.navTo(`/${URLPRIFIX}/${companyId}/accounts/journal/create`);
                        }}
                    >
                        ➕ ADD JOURNAL
                    </button>
                )}
            </div>

            <div className="row-span-9 h-full w-full">
                <div className="py-5 px-[30px] h-full w-full">
                    <ListViewUp
                        header={header}
                        filteredRecords={records}
                        Tablehead={Tablehead}
                        Tablerecord={Tablerecord}
                        sortConfig={sortConfig}
                        handleSort={handleSort}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        store={store}
                        handleOnSelect={handleOnSelect}
                        isSelected={isSelected}
                        handleClickRow={handleClickRow}
                        handleDelete={(item) => {/* Implement Delete Logic if needed */}}
                        handleSelectAll={(e) => handleOnSelect(records, e.target.checked)}
                        isAllSelected={records.length > 0 && selectedRecords.length === records.length}
                        tablename={tablename}
                    />
                    
                    <BulkActionsBar
                        selectedRecords={selectedRecords}
                        store={store}
                        onBulkDelete={() => {/* Implement Bulk Delete */}}
                    />
                    
                    <div className="float-right mr-8 mt-6">
                        <Pagination onchange={(page) => loadListview({ page })} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JournalsListUpdated;